<template>
  <div class="post-webwidget-v2-player-audio-player" :class="audioPlayerClassList">
    <audio
      ref="audioPlayer"
      :autoplay="autoplay"
      @abort="onAbort"
      @canplay="onCanPlay"
      @durationchange="onDurationChange"
      @emptied="onEmptied"
      @ended="onEnded"
      @error="onError"
      @loadeddata="onLoadedData"
      @pause="onPause"
      @play="onPlay"
      @playing="onPlaying"
      @progress="onProgress"
      @seeked="onSeeked"
      @seeking="onSeeking"
      @stalled="onStalled"
      @suspend="onSuspend"
      @timeupdate="onTimeUpdate"
      @volumechange="onVolumeChange"
      @waiting="onWaiting"
    >
      <source :src="audioFileSrc" @error="onError" />
    </audio>
    <div class="post-webwidget-v2-player-audio-player-controls">
      <div class="post-webwidget-v2-player-audio-player-controls-left-side">
        <div class="post-webwidget-v2-player-audio-player-controls__play-toggle" @click="onPlayToggleButton">
          <img :src="playToggleButton" width="30" height="30" />
        </div>
      </div>
      <div class="post-webwidget-v2-player-audio-player-controls-right-side">
        <div class="post-webwidget-v2-player-audio-player-progress-bar" @click="onProgressBarClick">
          <!--
           <div class="post-webwidget-v2-player-audio-player-progress-bar__buffered" :style="bufferedBarStyle"></div>
          -->
          <div class="post-webwidget-v2-player-audio-player-progress-bar__played" :style="progressBarStyle"></div>
        </div>
        <div class="post-webwidget-v2-player-audio-player-progress-text">
          <template v-if="audioFileSrc">
            <div>
              <!-- 🔴 D-160 — the give-up needs a state of its own, because BOTH
                   branches below are lies once the engine has stopped: no data yet
                   reads "Buffering..." for the rest of the pageview, and a give-up
                   that lands mid-stream reads "LIVE" beside a Play glyph for a
                   stream that has been destroyed. `notifyUnrecoverable`'s stated
                   purpose is to let the component clear its spinner, and this is the
                   spinner it never reached.
                   🔑 Keyed on `mediaFailed && !canPlay`, not `mediaFailed` alone.
                   The latch is cleared only by `resetData()` and by the retry tap —
                   never by recovery — so on `mediaFailed` alone this would sit over
                   working audio after a transient error resolved, trading one
                   permanent lie for another. `onError` writes the two together, so
                   the pair is exactly the window in which the retry branch of
                   `onPlayToggleButton` is live. -->
              <template v-if="mediaFailed && !canPlay">
                <div>Unavailable — tap to retry</div>
              </template>
              <template v-else-if="loadedMediaData">
                <div v-if="playingLiveStream">LIVE</div>
                <div v-else>{{ formattedTimePlayed }} / {{ formattedDuration }}</div>
              </template>
              <template v-else>
                Buffering...
              </template>
            </div>
          </template>
          <template v-else>
            <div>Select an episode</div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// This file should be refactored.

import { reportNotAllowedIfApplicable, reportPlayerRetry } from '../core/faro';
import { formatDurationClock } from '../utils/dateFormat';
// LP-2 / MR-76: live audio routes through the shared hls.js engine (LP-H/MR-75)
// instead of the retired Viblast runtime global.
import { attachLiveStream, detachLiveStream } from '../utils/hls_engine';
// Vue-1 `require('...svg')` -> ES-module imports so Vite resolves the assets
// (mirrors the v3 subtree). Small SVGs inline as data URIs (assetsInlineLimit),
// so the single-IIFE dist shape + gzip budget are preserved.
import playWhiteSvg from '../../static/icons/white/svg/play.svg';
import playGreySvg from '../../static/icons/grey/svg/play.svg';
import pauseWhiteSvg from '../../static/icons/white/svg/pause.svg';
import pauseGreySvg from '../../static/icons/grey/svg/pause.svg';

export default {
  name: 'live-audio-player',
  // Vue3 (LP-4/MR-78, the LP-2-deferred emits item): declare the $emitted events.
  // `play`/`pause` are the component's OWN forwarded events to its parent
  // (NowPlayingDetail `@play`/`@pause`) — declaring them, exactly as the v1
  // media_player and v4 PlayPause siblings do, binds them as component events
  // rather than falling through as native attrs on the root.
  emits: ['ready', 'episode-ended', 'play', 'pause'],
  props: {
    darkTheme: Boolean,
    audioFileSrc: String,
    autoplay: {
      type: Boolean,
      default: false,
    },
    playingLiveStream: Boolean,
  },
  data() {
    return {
      loadedMediaData: false,
      fileLoadProgress: undefined,
      buffered: undefined,
      fileSize: undefined,
      isPlaying: false,
      canPlay: false,
      // D-133 — a latch, because a failed load is otherwise indistinguishable from
      // "not loaded yet": both leave canPlay false, and onPlayToggleButton treats
      // that as "do nothing". Without this the play button is permanently inert.
      mediaFailed: false,
      // D-165 — a SECOND latch, and it exists only so the retry has an outcome.
      // `mediaFailed` says "the load failed"; this says "the listener asked for
      // another go and we do not yet know how it went". The next `canplay` after
      // a retry is a recovery and the next `error` is a second failure — but
      // without this flag both are indistinguishable from the ordinary first
      // load, so the retry would have a count and no success RATE.
      retryPending: false,
      duration: undefined,
      currentTime: undefined,
    };
  },
  computed: {
    audioPlayerClassList() {
      return {
        'post-webwidget-v2-player-audio-player--dark-theme': this.darkTheme,
      };
    },

    bufferedPercentage() {
      return (this.buffered / this.duration) * 100 || 0;
    },

    bufferedBarStyle() {
      return {
        width: `${this.bufferedPercentage}%`,
      };
    },

    progress() {
      return (this.currentTime / this.duration) || 0;
    },

    progressBarStyle() {
      return {
        width: `${this.progress * 100}%`,
      };
    },

    // scrubPosition() {
      // This should be used if a input[type="range"] is ever used
      // Seek position or current position
      // return this.progress;
    // },

    playToggleButton() {
      return this.isPlaying ? this.pauseButtonImage : this.playButtonImage;
    },

    playButtonImage() {
      return this.darkTheme ? playWhiteSvg : playGreySvg;
    },

    pauseButtonImage() {
      return this.darkTheme ? pauseWhiteSvg : pauseGreySvg;
    },

    formattedTimePlayed() {
      // TODO: If over an hour show hours format
      return formatDurationClock((this.currentTime || 0) * 1000);
    },

    formattedDuration() {
      // TODO: If over an hour show hours format
      return formatDurationClock(this.duration * 1000);
    },

    useM4a() {
      return this.audioFileSrc && this.audioFileSrc.includes('m4a');
    },
  },
  methods: {
    resetData() {
      this.loadedMediaData = false;
      this.isPlaying = false;
      this.canPlay = false;
      this.currentTime = undefined;
      this.duration = undefined;
      // D-138 — the failure latch MUST be cleared here too. Without it a latch armed
      // once stayed armed for the life of the component, across every episode and
      // stream change.
      this.mediaFailed = false;
      // 🔑 D-165 — and so must the retry latch, for D-138's exact reason. A
      // pending retry that survives a source change makes the NEXT episode's
      // ordinary first `canplay` report itself as the previous episode's retry
      // recovering — a permanent, silent inflation of the success rate the flag
      // was added to measure. Every latch this component has grown so far has
      // had this bug once; this one is born without it.
      this.retryPending = false;
    },
    // D-165 — the retry is reported through the SHARED reporter in `core/faro.js`,
    // not from here. v2, v3 and v4 all emit this event; three inline copies of an
    // attribute set is where a schema drifts, and this module already imports that
    // file for the other cross-version reporter. The reasoning about phases and
    // about why no url rides the event lives with the helper.
    reportRetry(phase) {
      reportPlayerRetry(phase, {
        playerVersion: 'v2',
        sourceKind: this.useM4a ? 'm4a' : 'hls',
        live: this.playingLiveStream,
      });
    },
    // D-165 — settle a pending retry exactly once, whichever way it went.
    settleRetry(phase) {
      if (!this.retryPending) return;
      this.retryPending = false;
      this.reportRetry(phase);
    },
    onAbort() {
      //
    },
    onCanPlay() {
      this.canPlay = true;
      this.settleRetry('recovered');
    },
    onDurationChange({ target }) {
      this.duration = target.duration;
    },
    onEmptied() {
      this.resetData();
    },
    onEnded() {
      this.isPlaying = false;
      this.$emit('episode-ended');
    },
    onError(event) {
      // eslint-disable-next-line no-console
      console.log(event);
      // D-133 — this used to be the log ALONE, which is why a failed load left the
      // play button dead: `onPlayToggleButton` early-returns on `!canPlay`, and
      // nothing here distinguished "failed" from "not ready yet". Bound on BOTH the
      // media element and the <source>, because a <source> failure does not bubble
      // (the engine fact D-105 measured: the src-IDL path reports at the element,
      // the source-children path reports at the source).
      // 🔴 D-138 — ignore the error chromium fires at a src-LESS `<source>`.
      // `audioFileSrc` is a String prop with no default, so before a stream is chosen
      // the template renders `<audio><source></audio>`; per the resource-selection
      // algorithm that is a failed candidate and chromium fires `error` AT THE SOURCE
      // unprompted — no `load()`, no user action. MEASURED on mount with no
      // interaction: exactly one source error, `hasSrcAttr:false`,
      // networkState 3 (NETWORK_NO_SOURCE).
      //
      // D-133 bound that event and so armed `mediaFailed` on EVERY pageview, which
      // misrouted the first tap in any `!canPlay` window into the retry branch — most
      // visibly on the live/HLS path, where `canPlay` stays false until hls.js buffers.
      const el = event && event.target;
      if (el && el.tagName === 'SOURCE' && !el.getAttribute('src')) return;

      this.mediaFailed = true;
      this.canPlay = false;
      this.isPlaying = false;
      // 🔴 D-160 — and `loadedMediaData`, which nobody derived. It is written only
      // here-adjacent: `resetData()` clears it and `onLoadedData()` sets it, so a
      // give-up mid-stream left it TRUE and the transport went on rendering its
      // LIVE/time branch for a destroyed stream. The population is `resetData()`'s
      // own list — every member of it now has a stated give-up behaviour, derived and
      // checked in `test/unit-vue/v2-transport-truth.spec.js` rather than listed here
      // where it would rot.
      //
      // 🪤 D-173 — `currentTime`/`duration` stay KEPT, but the reason I first gave was
      // FALSE and refuted 200-odd lines up in this same file. It said they "render only
      // inside the branch this clears, so they cannot reach the listener". They also
      // feed `progress` -> the `progressBarStyle` computed, bound to
      // `.post-webwidget-v2-player-audio-player-progress-bar__played` at :38 — a div
      // OUTSIDE the `loadedMediaData` branch, rendered on every mount. So they reach
      // the listener on every pageview. Same shape as D-162: a claim written a short
      // scroll from its own refutation.
      //
      // 🔑 The classification survives on a DIFFERENT and true argument. The progress
      // bar showing where playback stopped is HONEST — the media did fail there, and
      // snapping it to 0 would imply the episode never played. And on the live path the
      // question does not arise: `duration` is Infinity or NaN for a live stream, so
      // `progress` is `|| 0` and the bar is already empty. Kept, for the reason that is
      // actually true.
      this.loadedMediaData = false;
      // D-165 — if this failure followed a retry, that is the retry's outcome.
      // Reported LAST, after the state above is settled, so a throw in telemetry
      // could never cost the component its give-up handling.
      this.settleRetry('failed');
    },
    onLoadedData({ target }) {
      this.loadedMediaData = true;
      this.duration = target.duration;
    },
    onPause() {
      this.isPlaying = false;
      this.$emit('pause');
    },
    onPlay() {
      this.canPlay = true;
      this.isPlaying = true;
      this.$emit('play');
    },
    onPlaying() {
      //
    },
    onProgress() {
      // this.buffered = target.buffered.end(0);
    },
    onProgressBarClick(event) {
      if (this.playingLiveStream) {
        return;
      }
      const seekToProgressPercentage = event.offsetX / event.target.offsetWidth;
      const newCurrentTime = seekToProgressPercentage * this.duration;
      this.$refs.audioPlayer.currentTime = newCurrentTime;
    },
    onSeeked() {
      //
    },
    onSeeking() {
      //
    },
    onStalled() {
      //
    },
    onSuspend() {
      //
    },
    onTimeUpdate(event) {
      this.currentTime = event.target.currentTime;
    },
    onVolumeChange() {
      //
    },
    onWaiting() {
      //
    },
    // 🔴 D-165 — the retry has to take the SAME branch the attach took, or it
    // hands the element to an engine that does not own it.
    //
    // D-133 made the retry `audioPlayer.load()` — a bare NATIVE load — and D-148
    // made that load able to reach the network instead of dying on a revoked
    // blob. Neither made it able to PLAY, because every non-m4a source v2 hands
    // the element is an HLS manifest and a native decoder cannot read one.
    // MEASURED: chromium answers `'maybe'` to
    // `canPlayType('application/vnd.apple.mpegurl')` and then fails with
    // MEDIA_ERR_SRC_NOT_SUPPORTED anyway, so the retry ended exactly where it
    // started, one round-trip later. Safari and iOS have native HLS and DO
    // recover — which is why this survived hand-testing.
    //
    // 🔑 THE GATE IS `useM4a`, NOT `playingLiveStream`, and that is deliberately
    // wider than the finding as filed. The register said to re-attach "when the
    // source is a live stream, which v2 can tell from its `playingLiveStream`
    // prop". The call graph says that misses half the population:
    // `NowPlayingSection.episodeAudioFileSrc` returns `liveStreamUrl` only when
    // `canPlayLiveStream`, and otherwise returns `m4aUrl` when the embed sets
    // `useM4a` — which DEFAULTS OFF — or `m3u8Url` when it does not. So an
    // ordinary on-demand episode in a default embed is a `.m3u8` that this
    // component's own watcher hands to `attachLiveStream`. Gating on
    // `playingLiveStream` would have fixed the live path and left the identical
    // defect on every episode, to be re-found later as a new finding.
    //
    // Reusing the watcher's own discriminator is what makes that class of
    // divergence impossible: the retry cannot disagree with the attach about
    // which engine owns the element, because it asks the same question.
    retryLoad() {
      const audioPlayer = this.$refs.audioPlayer;
      if (this.useM4a) {
        // The m4a bypass is native on the way in, so it is native on the way
        // back: `load()` re-runs resource selection on the src the watcher set.
        audioPlayer.load();
        return;
      }
      // Stop-before-(re)attach, exactly as the watcher does. `attachLiveStream`
      // tears down internally too, but going through the same pair here keeps
      // one shape for "put this source on this element".
      detachLiveStream(audioPlayer);
      // 🔑 `autoplay: true`, and ONLY here. The watcher forwards the `autoplay`
      // PROP because it fires on a source change — not a user gesture, and
      // subject to autoplay policy. This path is a click on the play control:
      // the listener has asked for audio, and a re-attach that leaves silence
      // would make them tap a second time for no reason they can see. A rejected
      // `play()` is already caught and reported inside the engine (D-151/D-207),
      // so the optimistic call cannot surface as an unhandled rejection.
      try {
        attachLiveStream(audioPlayer, this.audioFileSrc, { autoplay: true });
      } catch (e) {
        // Parity with the watcher's belt-and-suspenders catch; the engine is
        // already throw-safe.
      }
    },
    onPlayToggleButton() {
      if (!this.canPlay) {
        // D-133 — a tap after a failed load used to do NOTHING, forever. Retry the
        // load instead: the control the listener can see must be able to act. The
        // latch is cleared so a second failure re-arms rather than looping.
        if (this.mediaFailed) {
          this.mediaFailed = false;
          this.retryPending = true;
          this.reportRetry('attempted');
          this.retryLoad();
        }
        return;
      }

      if (this.$refs.audioPlayer.paused) {
        Promise.resolve(this.$refs.audioPlayer.play())
        .then(() => {
          this.isPlaying = true;
        })
        .then(null, (error) => {
          // 🔴 D-203 — thenable-safe WITHOUT collapsing to `.then(f, h)`.
          //
          // The chained form was kept on purpose: in `.then(f).catch(h)` the handler also
          // catches a throw from `f`, and collapsing to `.then(f, h)` silently drops that.
          // But `.play()` may return a host-patched thenable with no `.catch` (D-176), so
          // the chain could not be left as it was.
          //
          // 🪤 `.play().then(f).then(null, h)` DOES NOT FIX IT. On a minimal thenable,
          // `T.then(f)` returns whatever that `then` returns — possibly `undefined` — and
          // `.then(null, h)` on undefined throws the same TypeError one link further down.
          // Wrapping FIRST is what makes the rest of the chain native and therefore safe.
          //
          // 🔑 AND ONCE WRAPPED, `.catch(h)` WOULD BE EQUALLY SAFE -- `.catch(h)` IS
          // DEFINED as `.then(undefined, h)`, so on the native promise the wrap returns
          // the two forms are identical. `.then(null, h)` is used here for consistency
          // with the rest of the tree, a convention pinned since D-176 by
          // test/unit/play-rejection-handler-shape.spec.js. Do NOT read this block as
          // "`.catch` is unsafe on a native promise" -- it is not, and an earlier
          // version of this comment implied exactly that.
          // 🔴 D-146 — v2 reports the NotAllowed measurement; it was v4-only.
          // ⚠️ WITHOUT a station, deliberately. This component has no `station` prop —
          // the value lives two parents up in NowPlayingSection — and threading it
          // through NowPlayingDetail for an OPTIONAL attribution field is more surface
          // than the attribution is worth. An unattributed count is still a count,
          // where today there is none at all. If multi-station v2 embeds ever need the
          // split (the MED-5 concern), pass the prop then.
          reportNotAllowedIfApplicable(error);
          // eslint-disable-next-line no-console
          console.error(error);
        });
      } else {
        this.$refs.audioPlayer.pause();
      }
    },
  },
  mounted() {
    this.$emit('ready', this.$refs.audioPlayer);
  },
  beforeUnmount() {
    // LP-4/MR-78 (fidelity fold, closes PS-12): the Vue-1 original had no
    // teardown, so a torn-down player left any live hls.js session attached.
    // Mirror the v4 Player sibling and detach the live engine on unmount
    // (detachLiveStream is null-safe + throw-safe). v2 has no pauseMedia wrapper
    // — the <audio> is destroyed with the component — so the detach IS the
    // teardown parity.
    detachLiveStream(this.$refs.audioPlayer);
  },
  watch: {
    audioFileSrc(newStreamSrc) {
      // The master guard that early-returned when the Viblast global was absent was intentionally dropped: the shared engine helper is throw-safe.
      this.resetData();

      const audioPlayer = this.$refs.audioPlayer;
      // Stop-before-(re)attach: always tear down any live session first (was
      // the Viblast stop() call the setup() was always preceded by).
      // detachLiveStream is idempotent + throw-safe.
      detachLiveStream(audioPlayer);

      if (this.useM4a) {
        // m4a bypass stays NATIVE — hls.js must never attach for m4a (the old
        // note: "Viblast doesn't support m4a playback").
        audioPlayer.setAttribute('src', newStreamSrc);
        // 🔴 D-176 — `.then(null, handler)`, not `.catch(handler)`: a patched `play()`
        // may return a thenable without `.catch`, and calling it throws TypeError at the
        // moment playback already failed. See startPlayback in hls_engine.js.
        audioPlayer.play()
          .then(null, (e) => {
            // D-146 — the m4a bypass rejects the same way.
            reportNotAllowedIfApplicable(e);
            // eslint-disable-next-line no-console
            console.error(e);
          });
        return;
      }

      // Live HLS via the shared engine (LP-H/MR-75), replacing viblast.setup().
      // The `key` (Viblast license) + `forceHEAAC` drop — no hls.js equivalent
      // (HE-AAC is handled by hls.js's transmuxer). attachLiveStream is already
      // throw-safe (it swallows the iOS quirk the old setup was try/caught for);
      // the try/catch is kept for belt-and-suspenders parity with the original.
      try {
        attachLiveStream(audioPlayer, newStreamSrc, { autoplay: this.autoplay });
      } catch (e) {
        //
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../assets/variables-v2';
.post-webwidget-v2-player-audio-player {
  &-controls {
    display: flex;
    width: 100%;
    align-items: center;

    &-left-side {
      margin-right: 1em;
    }

    &-right-side {
      display: flex;
      flex-direction: column;
      width: 100%;
      justify-content: center;
    }


    &__play-toggle {
      cursor: pointer;

      // R11 (a11y): the play/pause glyph renders 30x33 on a phone, under the
      // plan's 44px touch-target figure (§7.4). Expand the HIT AREA only —
      // transparent and out of flow, so the control bar is pixel-identical.
      // It is the only target in `-left-side`, which is separated from the
      // progress column by a 1em margin, so a 44px box has nothing to steal
      // from. @media(max-width)-gated ONLY.
      // ⚠️ NOTE THE CEILING: this is v2's existing $mobileBreakpoint (400px),
      // not the 480px every other R11 block uses — deliberately, so v2 extends
      // its own established ladder rather than layering a second breakpoint
      // (RULE 3). The cost is a real 401-480px gap: on a 414px iPhone Plus or a
      // 428px Pro Max column, v3/v4 transport buttons get their grown hit areas
      // and v2's play toggle stays at its base size. Accepted; recorded so it is
      // a known gap rather than an oversight.
      @media screen and (max-width: $mobileBreakpoint) {
        position: relative;

        &::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 44px;
          height: 44px;
        }
      }
    }
  }

  &-progress-text {
    display: flex;
    justify-content: space-between;
    width: 100%;
    font-size: 0.7em;
  }

  &-progress-bar {
    margin-bottom: 0.5em;
    position: relative;
    cursor: pointer;
    width: 100%;
    height: 10px;
    background: $audioPlayerProgressBackground;
    border-radius: 2.5px; // half of $borderRadius
    box-shadow: 0px 0px 1px $borderColor;

    /*
    &__buffered {
      position: absolute;
      height: 100%;
      background: $audioPLayerBufferedProgressBackground;
      border-radius: 2.5px;
    }
    */

    &__played {
      pointer-events: none; // Neccessary to ignore click so the parent div can catch the event and determine where to seek
      position: absolute;
      z-index: 2;
      height: 100%;
      background: $audioPlayerPlayedProgressBackground;
      border-radius: 2.5px; // half of $borderRadius
    }
  }

  &--dark-theme {
    .post-webwidget-v2-player-audio-player {
      &-progress-bar {
        background: $darkThemeAudioPlayerProgressBackground;
        box-shadow: 0px 0px 1px $darkThemeBorderColor;
        &__played {
          background: $darkThemeAudioPlayerPlayedProgressBackground;
        }
      }
    }
  }
}
</style>
